import { useState, useEffect } from 'react'
import { calendarDateOf, todayInBusinessZone } from '../../functions-src/lib/calendar'
import { Modal } from './modal'
import type { ActionPlanWithOwner } from '@/types'

interface ActionPlanFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: ActionPlanFormData) => void
  plan?: ActionPlanWithOwner | null
  isPending: boolean
}

export interface ActionPlanFormData {
  title: string
  description: string
  expected_outcome: string
  status: 'draft' | 'active'
  start_date: string
  target_date: string
}

// Note: Owner is inherited from goal automatically - no owner dropdown per validation decision
export function ActionPlanForm({
  isOpen,
  onClose,
  onSubmit,
  plan,
  isPending,
}: ActionPlanFormProps) {
  const [form, setForm] = useState<ActionPlanFormData>({
    title: '',
    description: '',
    expected_outcome: '',
    status: 'draft',
    start_date: '',
    target_date: '',
  })

  // Populate form when editing
  useEffect(() => {
    if (plan) {
      setForm({
        title: plan.title,
        description: plan.description || '',
        expected_outcome: plan.expected_outcome || '',
        status: plan.status === 'draft' ? 'draft' : 'active',
        start_date: plan.start_date ? calendarDateOf(plan.start_date) : '',
        target_date: plan.target_date ? calendarDateOf(plan.target_date) : '',
      })
    } else {
      setForm({
        title: '',
        description: '',
        expected_outcome: '',
        status: 'draft',
        start_date: todayInBusinessZone(),
        target_date: '',
      })
    }
  }, [plan, isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(form)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={plan ? 'Edit Action Plan' : 'New Action Plan'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-cfa-dark-gray mb-1">
            Title *
          </label>
          <input
            type="text"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full rounded-lg border border-cfa-gray px-3 py-2 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
            placeholder="What needs to be done?"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-cfa-dark-gray mb-1">
            Description
          </label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full rounded-lg border border-cfa-gray px-3 py-2 h-20 resize-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
            placeholder="Detailed plan of action..."
          />
        </div>

        {/* Expected Outcome */}
        <div>
          <label className="block text-sm font-medium text-cfa-dark-gray mb-1">
            Expected Outcome
          </label>
          <input
            type="text"
            value={form.expected_outcome}
            onChange={(e) => setForm({ ...form, expected_outcome: e.target.value })}
            className="w-full rounded-lg border border-cfa-gray px-3 py-2 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
            placeholder="What does success look like?"
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-cfa-dark-gray mb-1">
            Status
          </label>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value as 'draft' | 'active' })}
            className="w-full rounded-lg border border-cfa-gray px-3 py-2 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
          </select>
        </div>

        {/* Date row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-cfa-dark-gray mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="w-full rounded-lg border border-cfa-gray px-3 py-2 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-cfa-dark-gray mb-1">
              Target Date
            </label>
            <input
              type="date"
              value={form.target_date}
              onChange={(e) => setForm({ ...form, target_date: e.target.value })}
              className="w-full rounded-lg border border-cfa-gray px-3 py-2 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-cfa-dark-gray bg-cfa-light-gray hover:bg-cfa-gray rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue/90 rounded-lg transition-colors disabled:opacity-50"
            disabled={isPending}
          >
            {isPending ? 'Saving...' : (plan ? 'Save' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
